import React, { useEffect, useState } from 'react';
import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Home, DollarSign, TrendingUp, Users, Bot, Handshake, Newspaper,
  CreditCard, Cog, BarChart3, Bell, UserCircle, LogOut, MapPin, PanelLeftClose, PanelLeft,
  Filter, BookOpen, PenSquare, Send, ShieldCheck, Accessibility, HeartPulse, Store, PlayCircle,
  Menu, X, History, Share2, Radio,
} from 'lucide-react';
import { OS_VERSION, OS_BUILD_DATE } from '../../version';
import { Toaster } from '../../components/ui/sonner'; // eslint-disable-line no-unused-vars
import { useT } from '../../i18n';
import { authFetch, useAuthUser, fetchMe } from '../../lib/auth';
import { applyTextZoom } from '../../components/TextSizeControl';
import { TextSizePill } from '../../os3/OsFooter';
import { useOs2, wIcon, RANGES } from './os2ui';
import { GlobalSearch } from './GlobalSearch';
import { useOsTheme, ThemeToggle } from '../../os3/theme';
import { isDemo, exitDemo } from '../../demo/demoMode';
import { TEAM_AVATARS } from '../../components/teamAvatars';
import RachelPush from '../../components/RachelPush';

const NAV = [
  { to: '', icon: Home, en: 'Executive Overview', es: 'Resumen Ejecutivo', end: true },
  { section: true, en: 'Growth', es: 'Crecimiento' },
  { to: 'pipeline', icon: Filter, en: 'Revenue Pipeline', es: 'Revenue Pipeline' },
  { to: 'growth', icon: TrendingUp, en: 'Growth Center', es: 'Growth Center' },
  { to: 'social', icon: Share2, en: 'Social Intelligence', es: 'Social Intelligence' },
  { to: 'content', icon: Newspaper, en: 'Marketing', es: 'Marketing' },
  { to: 'affiliates', icon: Handshake, en: 'Affiliates', es: 'Afiliados' },
  { to: 'partners', icon: Handshake, en: 'Partners', es: 'Partners' },
  { section: true, en: 'Company', es: 'Empresa' },
  { to: 'autonomy', icon: Bot, en: 'AI Executive Team', es: 'AI Executive Team' },
  { to: 'restaurants', icon: Store, en: 'Organization', es: 'Organización' },
  { to: 'finance', icon: CreditCard, en: 'Finance', es: 'Finanzas' },
  { section: true, en: 'Operations', es: 'Operación' },
  { to: 'system-health', icon: HeartPulse, en: 'Operations Center', es: 'Centro de Operaciones' },
  { to: 'reports', icon: BarChart3, en: 'Reports', es: 'Reportes' },
  { to: 'notifications', icon: Bell, en: 'Notifications', es: 'Notificaciones' },
  { section: true, en: 'Account', es: 'Cuenta' },
  { to: 'glossary', icon: BookOpen, en: 'Glossary', es: 'Glosario' },
  { to: 'changelog', icon: History, en: 'Version History', es: 'Historial de versiones' },
  { to: 'settings', icon: UserCircle, en: 'Profile / Settings', es: 'Perfil / Ajustes' },
];

const TOOLS = [
  { to: '/admin/editor', icon: PenSquare, en: 'Editorial Panel', es: 'Panel Editorial' },
  { to: '/demo/os', icon: PlayCircle, en: 'Client Demo', es: 'Demo de cliente' },
];

const ROLE_BADGE = {
  admin: ['Admin', 'bg-[#FFF7ED] text-[#b45309] border-orange-200'],
  affiliate: ['Affiliate', 'bg-sky-50 text-sky-700 border-sky-200'],
  client: ['Customer', 'bg-emerald-50 text-emerald-700 border-emerald-200'],
};

const MOBILE_NAV = [
  { to: '', icon: Home, en: 'Home', es: 'Inicio', end: true },
  { to: 'autonomy', icon: Bot, en: 'AI', es: 'AI' },
  { to: 'growth', icon: TrendingUp, en: 'Growth', es: 'Growth' },
  { to: 'pipeline', icon: Filter, en: 'CRM', es: 'CRM' },
];

function MobileBottomNav({ isEs, onMore, moreOpen }) {
  return (
    <nav className="md:hidden fixed bottom-0 inset-x-0 z-[60] border-t border-gray-200 bg-white/95 backdrop-blur-xl pb-[env(safe-area-inset-bottom)]" data-testid="aios-bottom-nav">
      <div className="grid grid-cols-5">
        {MOBILE_NAV.map((n) => (
          <NavLink key={n.to} to={`/admin/ai-os${n.to ? `/${n.to}` : ''}`} end={n.end}
            data-testid={`bottomnav-${n.to || 'home'}`}
            className={({ isActive }) => `flex flex-col items-center gap-0.5 pt-1.5 pb-1 text-[10px] font-bold transition-colors ${isActive && !moreOpen ? 'text-[#F77F11]' : 'text-gray-400'}`}>
            {({ isActive }) => (
              <>
                <span className={`flex items-center justify-center w-10 h-6 rounded-full transition-colors ${isActive && !moreOpen ? 'bg-orange-100' : ''}`}>
                  <n.icon className="w-[18px] h-[18px]" strokeWidth={2} aria-hidden="true" />
                </span>
                {isEs ? n.es : n.en}
              </>
            )}
          </NavLink>
        ))}
        <button onClick={onMore} data-testid="bottomnav-more"
          className={`flex flex-col items-center gap-0.5 pt-1.5 pb-1 text-[10px] font-bold transition-colors ${moreOpen ? 'text-[#F77F11]' : 'text-gray-400'}`}>
          <span className={`flex items-center justify-center w-10 h-6 rounded-full ${moreOpen ? 'bg-orange-100' : ''}`}>
            <Menu className="w-[18px] h-[18px]" strokeWidth={2} aria-hidden="true" />
          </span>
          {isEs ? 'Más' : 'More'}
        </button>
      </div>
    </nav>
  );
}

function SheetTextSize() {
  const [level, setLevel] = useState(() => parseInt(localStorage.getItem('kx_text_zoom') || '100', 10));
  const set = (v) => { setLevel(v); localStorage.setItem('kx_text_zoom', String(v)); applyTextZoom(v); };
  return (
    <div className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-white p-0.5">
      {[[100, 'text-[12px]'], [110, 'text-[14px]'], [120, 'text-[16px]']].map(([v, cls]) => (
        <button key={v} onClick={() => set(v)} aria-pressed={level === v} data-testid={`more-zoom-${v}`}
          className={`w-10 h-10 rounded-full font-bold leading-none transition-colors ${cls} ${level === v ? 'bg-[#F77F11] text-white' : 'text-gray-500 active:bg-gray-100'}`}>
          A
        </button>
      ))}
    </div>
  );
}

function MoreSheet({ isEs, open, onClose, demo, lang, setLang, logout }) {
  const startY = React.useRef(0);
  React.useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  if (!open) return null;
  const items = NAV.filter((n) => !n.section && !MOBILE_NAV.some((m) => m.to === n.to));
  return (
    <div className="md:hidden fixed inset-0 z-[70]" data-testid="aios-more-sheet">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} aria-hidden="true" />
      <div className="absolute bottom-0 inset-x-0 rounded-t-3xl bg-white shadow-2xl max-h-[78vh] overflow-y-auto pb-[calc(env(safe-area-inset-bottom)+16px)]" style={{ animation: 'kxSheetUp .25s ease-out' }}
        onTouchStart={(e) => { startY.current = e.touches[0].clientY; }}
        onTouchEnd={(e) => { if (e.changedTouches[0].clientY - startY.current > 70) onClose(); }}>
        <div className="sticky top-0 bg-white pt-2 pb-2 px-5 border-b border-gray-100 z-10">
          <span className="block mx-auto mb-2 w-10 h-1 rounded-full bg-gray-200" aria-hidden="true" />
          <div className="flex items-center justify-between">
            <p className="text-[13px] font-bold text-gray-900">{isEs ? 'Todos los módulos' : 'All modules'}</p>
            <button onClick={onClose} className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center" data-testid="more-sheet-close" aria-label={isEs ? 'Cerrar' : 'Close'}>
              <X className="w-4 h-4 text-gray-500" />
            </button>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2 p-4">
          {items.map((n) => (
            <NavLink key={n.to} to={`/admin/ai-os${n.to ? `/${n.to}` : ''}`} end={n.end} onClick={onClose}
              data-testid={`more-nav-${n.to || 'overview'}`}
              className={({ isActive }) => `flex items-center gap-2.5 rounded-2xl border p-3 text-[12.5px] font-semibold transition-colors ${isActive ? 'border-[#F77F11]/50 bg-orange-50 text-gray-900' : 'border-gray-100 text-gray-600 active:bg-gray-50'}`}>
              <n.icon className="w-[18px] h-[18px] shrink-0 text-[#F77F11]" strokeWidth={1.8} aria-hidden="true" />
              <span className="truncate">{isEs ? n.es : n.en}</span>
            </NavLink>
          ))}
          {!demo && TOOLS.map((t) => (
            <NavLink key={t.to} to={t.to} onClick={onClose} data-testid={`more-tool-${t.to.split('/').pop()}`}
              className="flex items-center gap-2.5 rounded-2xl border border-gray-100 p-3 text-[12.5px] font-semibold text-gray-600 active:bg-gray-50">
              <t.icon className="w-[18px] h-[18px] shrink-0 text-gray-400" strokeWidth={1.8} aria-hidden="true" />
              <span className="truncate">{isEs ? t.es : t.en}</span>
            </NavLink>
          ))}
        </div>
        <div className="px-5 pt-2 pb-1 flex items-center justify-between gap-3" data-testid="more-text-size">
          <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wide">{isEs ? 'Tamaño del texto' : 'Text size'}</p>
          <SheetTextSize />
        </div>
        <div className="px-5 pt-1 pb-2 flex items-center justify-between gap-3 border-t border-gray-100">
          <div className="flex items-center rounded-full border border-gray-200 bg-white p-0.5 text-[11.5px] font-semibold" data-testid="more-lang-switch">
            {['en', 'es'].map((l) => (
              <button key={l} onClick={() => setLang(l)}
                className={`px-3 py-1.5 rounded-full uppercase ${lang === l ? 'bg-gray-900 text-white' : 'text-gray-400'}`}
                data-testid={`more-lang-${l}`}>
                {l}
              </button>
            ))}
          </div>
          <a href="/hub" className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-gray-500" data-testid="more-back-to-hub">
            <Home className="w-4 h-4" aria-hidden="true" /> {isEs ? 'Volver al Hub' : 'Back to Hub'}
          </a>
          <button onClick={logout} className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-gray-500" data-testid="more-logout">
            <LogOut className="w-4 h-4" aria-hidden="true" /> {isEs ? 'Salir' : 'Log out'}
          </button>
        </div>
      </div>
    </div>
  );
}

function FloatingRachel({ isEs, pathname }) {
  const navigate = useNavigate();
  if (pathname.startsWith('/admin/ai-os/autonomy')) return null;
  const rachel = TEAM_AVATARS.find((a) => a.key === 'rachel');
  return (
    <button onClick={() => navigate('/admin/ai-os/autonomy?tab=team&agent=rachel')}
      className="fixed right-4 bottom-[84px] md:bottom-4 z-[55] flex items-center gap-2 rounded-full bg-gray-900 text-white pl-1.5 pr-4 py-1.5 shadow-[0_12px_32px_rgba(0,0,0,0.30)] hover:scale-[1.04] active:scale-95 transition-transform"
      data-testid="floating-rachel-btn" aria-label={isEs ? 'Hablar con Rachel' : 'Talk to Rachel'}>
      <span className="relative shrink-0">
        {rachel ? (
          <img src={rachel.img} alt="Rachel" className="w-9 h-9 rounded-full object-cover" />
        ) : (
          <span className="flex w-9 h-9 rounded-full bg-[#F77F11] items-center justify-center text-[13px] font-bold">R</span>
        )}
        <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-emerald-400 border-2 border-gray-900" aria-hidden="true" />
      </span>
      <span className="text-[12.5px] font-bold">Rachel</span>
    </button>
  );
}

function TextZoom({ isEs, collapsed }) {
  const [level, setLevel] = useState(() => parseInt(localStorage.getItem('kx_text_zoom') || '100', 10));
  useEffect(() => { applyTextZoom(level); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const set = (v) => {
    setLevel(v);
    localStorage.setItem('kx_text_zoom', String(v));
    applyTextZoom(v);
  };
  if (collapsed) return null;
  return (
    <div className="inline-flex items-center gap-0.5 rounded-full border border-gray-200 bg-white p-0.5" role="group"
      aria-label={isEs ? 'Tamaño del texto' : 'Text size'} title={isEs ? 'Tamaño del texto' : 'Text size'}
      data-testid="aios-accessibility">
      {[[100, 'text-[11px]'], [110, 'text-[13px]'], [120, 'text-[15px]']].map(([v, cls]) => (
        <button key={v} onClick={() => set(v)} aria-pressed={level === v}
          className={`w-7 h-7 rounded-full font-bold leading-none transition-colors ${cls} ${
            level === v ? 'bg-[#F77F11] text-white' : 'text-gray-500 hover:bg-gray-100'}`}
          data-testid={`aios-textsize-${v}`}>
          A
        </button>
      ))}
    </div>
  );
}

function greeting(isEs) {
  const h = new Date().getHours();
  if (h < 12) return isEs ? 'Buenos días' : 'Good morning';
  if (h < 19) return isEs ? 'Buenas tardes' : 'Good afternoon';
  return isEs ? 'Buenas noches' : 'Good evening';
}

export default function AiOsLayout() {
  const { lang, setLang } = useT();
  const isEs = lang === 'es';
  const [collapsed, setCollapsed] = useState(() => (typeof window !== 'undefined' && window.innerWidth < 1024));
  const [moreOpen, setMoreOpen] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const user = useAuthUser();
  const [theme, setTheme] = useOsTheme();
  const demo = isDemo();
  const { data: hdr } = useOs2('/header');
  const w = hdr?.weather;
  const current = NAV.find((n) => (n.end ? pathname === '/admin/ai-os' : pathname.startsWith(`/admin/ai-os/${n.to}`)));
  const [roleLabel, roleCls] = ROLE_BADGE[user?.role] || ROLE_BADGE.admin;

  const logout = async () => {
    try { await authFetch('/auth/logout', { method: 'POST' }); } catch { /* noop */ }
    navigate('/login');
  };

  return (
    <div className="h-screen flex overflow-hidden os3-scope" style={{ background: 'var(--os-bg)', color: 'var(--os-ink)' }} data-testid="aios-layout">
      {demo && (
        <div className="fixed bottom-[76px] md:bottom-4 left-3 md:left-1/2 md:-translate-x-1/2 z-[90] flex items-center gap-2 md:gap-3 rounded-full px-3.5 md:px-4 py-2 bg-gray-900 text-white shadow-2xl" data-testid="demo-banner">
          <span className="w-2 h-2 rounded-full bg-[#F77F11] animate-pulse" aria-hidden />
          <span className="text-[12px] font-bold tracking-wide">
            <span className="sm:hidden">DEMO</span>
            <span className="hidden sm:inline">{isEs ? 'MODO DEMO · datos de muestra' : 'DEMO MODE · sample data'}</span>
          </span>
          <button onClick={async () => {
            exitDemo();
            try {
              const me = await fetchMe();
              window.location.href = me ? '/admin/ai-os' : '/';
            } catch { window.location.href = '/'; }
          }}
            className="text-[12px] font-semibold text-white/70 hover:text-white underline underline-offset-2" data-testid="demo-exit">
            {isEs ? 'Salir' : 'Exit'}
          </button>
        </div>
      )}
      {/* Sidebar (desktop) */}
      <aside className={`${collapsed ? 'w-[68px]' : 'w-64'} hidden md:flex flex-shrink-0 border-r flex-col transition-[width] duration-300 z-20`}
        style={{ background: 'var(--os-bg)', borderColor: 'var(--os-border)' }} data-testid="aios-sidebar">
        <NavLink to="" end className={`h-14 flex items-center gap-2.5 px-4 border-b border-gray-200 hover:bg-orange-50/50 transition-colors ${collapsed ? 'justify-center px-0' : ''}`}
          title={isEs ? 'Ir al inicio del dashboard' : 'Go to dashboard home'} data-testid="aios-logo-home">
          <img src="/kitxens-logo.png" alt="Kitxens" className="w-8 h-8 rounded-lg shrink-0" />
          {!collapsed && (
            <div className="min-w-0">
              <p className="text-[15px] font-semibold tracking-tight leading-none">kit<span className="text-[#F77F11]">x</span>ens</p>
              <p className="text-[10px] tracking-[0.18em] uppercase font-medium mt-0.5" style={{ color: 'var(--os-ink-faint, #9CA3AF)' }}>
                AI OS <span className="font-bold tracking-normal normal-case" style={{ color: 'var(--os-ink, #1F2937)' }} data-testid="aios-version">{OS_VERSION}</span>
                <span className="tracking-normal normal-case" style={{ color: 'var(--os-ink-soft, #6B7280)' }}> · {OS_BUILD_DATE.slice(5).split('-').reverse().join('/')}</span>
              </p>
            </div>
          )}
        </NavLink>
        <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-0.5">
          {NAV.map((n, idx) => n.section ? (
            !collapsed && <p key={`sec-${idx}`} className="pt-4 pb-1 px-3 text-[10px] tracking-[0.18em] uppercase text-gray-300 font-bold">{isEs ? n.es : n.en}</p>
          ) : (
            <NavLink
              key={n.to}
              to={`/admin/ai-os${n.to ? `/${n.to}` : ''}`}
              end={n.end}
              title={isEs ? n.es : n.en}
              data-testid={`sidebar-nav-${n.to || 'overview'}`}
              className={({ isActive }) =>
                `relative flex items-center gap-3 rounded-lg px-3 py-2 text-[13.5px] font-medium transition-all duration-200 ${collapsed ? 'justify-center px-0' : ''} ${
                  isActive
                    ? 'bg-white text-gray-900 border border-gray-200 shadow-[0_1px_2px_rgba(0,0,0,0.04)]'
                    : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100/80 hover:translate-x-0.5 border border-transparent'
                }`}
            >
              {({ isActive }) => (
                <>
                  {isActive && !collapsed && <span className="absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-full bg-[#F77F11]" aria-hidden="true" />}
                  <n.icon className={`w-[18px] h-[18px] shrink-0 transition-transform duration-200 group-hover:scale-110 ${isActive ? 'text-[#F77F11]' : 'text-gray-400'}`} strokeWidth={1.7} aria-hidden="true" />
                  {!collapsed && <span className="truncate">{isEs ? n.es : n.en}</span>}
                  {isActive && !collapsed && <span className="ml-auto w-1.5 h-1.5 rounded-full bg-[#F77F11]" aria-hidden="true" />}
                </>
              )}
            </NavLink>
          ))}
          {!collapsed && !demo && (
            <p className="pt-4 pb-1 px-3 text-[10px] tracking-[0.18em] uppercase text-gray-300 font-bold">{isEs ? 'Herramientas' : 'Tools'}</p>
          )}
          {(demo ? [] : TOOLS).map((t) => (
            <NavLink key={t.to} to={t.to} title={isEs ? t.es : t.en}
              data-testid={`sidebar-tool-${t.to.split('/').pop()}`}
              className={`flex items-center gap-3 rounded-lg px-3 py-2 text-[13.5px] font-medium text-gray-500 hover:text-gray-900 hover:bg-gray-100/80 hover:translate-x-0.5 transition-all duration-200 border border-transparent ${collapsed ? 'justify-center px-0' : ''}`}>
              <t.icon className="w-[18px] h-[18px] shrink-0 text-gray-400" strokeWidth={1.7} aria-hidden="true" />
              {!collapsed && <span className="truncate">{isEs ? t.es : t.en}</span>}
            </NavLink>
          ))}
          {!demo && (
            <a href="/hub" title={isEs ? 'Volver al Hub' : 'Back to Hub'} data-testid="aios-back-to-hub"
              className={`mt-2 flex items-center gap-3 rounded-lg px-3 py-2 text-[13.5px] font-semibold text-gray-500 hover:text-[#F77F11] hover:bg-orange-50 transition-all duration-200 border-t border-gray-100 pt-3 ${collapsed ? 'justify-center px-0' : ''}`}>
              <Home className="w-[18px] h-[18px] shrink-0" strokeWidth={1.7} aria-hidden="true" />
              {!collapsed && <span className="truncate">{isEs ? '← Volver al Hub' : '← Back to Hub'}</span>}
            </a>
          )}
        </nav>
        <div className={`p-2 border-t border-gray-200 flex items-center gap-1 ${collapsed ? 'justify-center' : 'justify-end'}`}>
          <button onClick={() => setCollapsed(!collapsed)}
            title={collapsed ? (isEs ? 'Expandir' : 'Expand') : (isEs ? 'Contraer' : 'Collapse')}
            className="flex items-center justify-center rounded-lg px-2.5 py-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
            data-testid="aios-sidebar-toggle">
            {collapsed ? <PanelLeft className="w-4 h-4" /> : <PanelLeftClose className="w-4 h-4" />}
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col h-full overflow-hidden">
        <header className="h-14 shrink-0 border-b border-gray-200 bg-white/85 backdrop-blur-xl flex items-center justify-between px-3 sm:px-6 z-10" data-testid="aios-header">
          <div className="min-w-0">
            <p className="text-[14px] sm:text-[15px] font-semibold tracking-tight text-gray-900 truncate flex items-center gap-2" data-testid="aios-greeting">
              {greeting(isEs)}, {hdr?.name || '…'}
              <span className={`hidden sm:inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-bold uppercase tracking-wide ${roleCls}`} data-testid="aios-role-badge">
                <ShieldCheck className="w-3 h-3" aria-hidden="true" /> {roleLabel}
              </span>
            </p>
            <p className="text-[11.5px] text-gray-400 truncate" data-testid="aios-breadcrumb">
              <a href="/hub" className="hidden sm:inline hover:text-[#F77F11] transition-colors font-semibold" title={isEs ? 'Ir a Kitxens OS (inicio)' : 'Go to Kitxens OS (home)'} data-testid="aios-hub-link">Kitxens AI OS</a><span className="hidden sm:inline text-gray-300"> › </span>
              <span className="text-[#F77F11] font-semibold">{current ? (isEs ? current.es : current.en) : ''}</span>
              <span className="text-gray-300"> · </span>{new Date().toLocaleDateString(isEs ? 'es' : 'en', { weekday: 'long', month: 'long', day: 'numeric' })}
            </p>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            <GlobalSearch isEs={isEs} />
            <span className="hidden md:inline-flex"><TextSizePill isEs={isEs} compact /></span>
            <ThemeToggle theme={theme} setTheme={setTheme} isEs={isEs} />
            <select
              value={localStorage.getItem('kx_os2_days') || ''}
              onChange={(e) => { localStorage.setItem('kx_os2_days', e.target.value); window.dispatchEvent(new Event('kx-os2-range')); }}
              className="hidden sm:block h-8 px-2.5 rounded-full border border-gray-200 bg-white text-[12px] font-semibold text-gray-600 focus:outline-none cursor-pointer"
              data-testid="aios-range-selector"
              aria-label={isEs ? 'Rango de fechas' : 'Date range'}>
              {RANGES.map(([v, en, es]) => <option key={v} value={v}>{isEs ? es : en}</option>)}
            </select>
            {hdr?.city && (
              <span className="hidden sm:flex flex-nowrap items-center gap-1.5 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-[12.5px] text-gray-600 whitespace-nowrap" data-testid="aios-weather-chip">
                <MapPin className="w-3.5 h-3.5 text-[#F77F11] shrink-0" aria-hidden="true" />
                <span className="whitespace-nowrap">{hdr.city}</span>
                {w && <>
                  <span className="inline-flex flex-nowrap items-center gap-1 font-semibold text-gray-900 whitespace-nowrap" data-testid="aios-weather-temp">
                    {wIcon(w.code)}
                    <span className="hidden xl:inline whitespace-nowrap">{Math.round(w.temp * 9 / 5 + 32)}°F <span className="text-gray-400 font-normal">/ {Math.round(w.temp)}°C</span></span>
                    <span className="xl:hidden whitespace-nowrap">{Math.round(w.temp * 9 / 5 + 32)}° <span className="text-gray-400 font-normal">/ {Math.round(w.temp)}°</span></span>
                  </span>
                  {w.days?.[1] && (
                    <span className="inline-flex flex-nowrap items-center gap-1 text-gray-400 border-l border-gray-200 pl-2 whitespace-nowrap">
                      {isEs ? 'mañana' : 'tmrw'} {wIcon(w.days[1].code, 'w-3.5 h-3.5')}
                      <span className="hidden xl:inline whitespace-nowrap">{Math.round(w.days[1].max * 9 / 5 + 32)}°F/{Math.round(w.days[1].max)}°C</span>
                      <span className="xl:hidden whitespace-nowrap">{Math.round(w.days[1].max * 9 / 5 + 32)}°/{Math.round(w.days[1].max)}°</span>
                    </span>
                  )}
                </>}
              </span>
            )}
            <div className="hidden md:flex items-center rounded-full border border-gray-200 bg-white p-0.5 text-[11.5px] font-semibold" data-testid="aios-lang-switch">
              {['en', 'es'].map((l) => (
                <button key={l} onClick={() => setLang(l)}
                  className={`px-2.5 py-1 rounded-full uppercase transition-colors ${lang === l ? 'bg-gray-900 text-white' : 'text-gray-400 hover:text-gray-700'}`}
                  data-testid={`aios-lang-${l}`}>
                  {l}
                </button>
              ))}
            </div>
            <button onClick={logout} className="hidden md:inline-flex items-center gap-1.5 text-[12.5px] font-medium text-gray-400 hover:text-gray-800 transition-colors" data-testid="aios-logout">
              <LogOut className="w-4 h-4" aria-hidden="true" /> {isEs ? 'Salir' : 'Log out'}
            </button>
          </div>
        </header>
        <main className="flex-1 overflow-y-auto">
          <div className="p-4 pb-28 md:p-6 md:pb-6 lg:p-8 max-w-[1400px] w-full mx-auto kx-zoomable">
            <Outlet />
          </div>
        </main>
      </div>
      <FloatingRachel isEs={isEs} pathname={pathname} />
      <RachelPush isEs={isEs} />
      <MobileBottomNav isEs={isEs} onMore={() => setMoreOpen((v) => !v)} moreOpen={moreOpen} />
      <MoreSheet isEs={isEs} open={moreOpen} onClose={() => setMoreOpen(false)} demo={demo} lang={lang} setLang={setLang} logout={logout} />
      {/* Sonner Toaster now mounted globally in App.js */}
    </div>
  );
}
